/**
 * Usage summary adapter (Task 9 frontend).
 *
 * `GET /api/billing/getUsageSummary` (services/quotaService.js on the
 * backend) returns a generic quota array (`quotas: [{code, unit, limit,
 * used, ...}]`) plus a per-section file-count breakdown
 * (`filesBySection: [{name, count}]`). This module adapts that into the
 * compact shape usagePanel.js actually renders, and centralises every
 * percent/remaining/format calculation so the component never does its
 * own arithmetic - one place to get NaN/Infinity/zero-limit/unlimited
 * handling right, not several.
 *
 * @typedef {Object} UsageSummary
 * @property {{ used: number, limit: number|null }} policies
 * @property {{ used: number, limit: number|null, bySection: Array<{name: string, count: number}> }} files
 */

const QUOTA_CODE = {
  POLICIES: 'POLICIES',
  STORAGE: 'DOCUMENT_STORAGE_BYTES',
};

/**
 * True when the raw API response actually carries usage data (a real
 * subscription, `quotas` returned as a non-empty array). False for "no
 * subscription yet" / a malformed response - the EMPTY state, distinct
 * from a subscription that legitimately has unlimited quotas (that has
 * rows, just with `limit: null`).
 */
export const hasUsageData = (raw) => Boolean(raw && Array.isArray(raw.quotas) && raw.quotas.length > 0);

/**
 * Finds one quota row by code and returns a used/limit pair. Missing
 * data (quota not returned, or usage not yet computed for it - see
 * quotaService.js's `used: null` convention for a quota type without a
 * live counter yet, e.g. LEADS) safely becomes 0/null rather than
 * throwing or propagating `undefined`. `limit: null` (unlimited) and
 * `limit: 0` (zero-limit) are both real, DIFFERENT values and both pass
 * through untouched - see usagePercent/usageRemaining below for why they
 * render differently.
 */
const pickQuota = (quotas, code) => {
  const row = Array.isArray(quotas) ? quotas.find(q => q && q.code === code) : null;
  const used  = Number.isFinite(row && row.used)  ? row.used  : 0;
  const limit = Number.isFinite(row && row.limit) ? row.limit : null;
  return { used, limit };
};

/**
 * Raw `GET /api/billing/getUsageSummary` response -> UsageSummary.
 * Tolerant of a missing/malformed response (returns the same zeroed
 * shape `hasUsageData` would call empty) so a transient bad response
 * degrades to an empty-looking summary, never a thrown error.
 *
 * @param {*} raw
 * @returns {UsageSummary}
 */
export const adaptUsageSummary = (raw) => {
  const quotas = raw && Array.isArray(raw.quotas) ? raw.quotas : [];
  const bySection = raw && Array.isArray(raw.filesBySection)
    ? raw.filesBySection
        .map(s => ({
          name  : String((s && s.name) || ''),
          count : Number.isFinite(s && s.count) ? s.count : 0,
        }))
        .filter(s => s.name)
    : [];

  return {
    policies : pickQuota(quotas, QUOTA_CODE.POLICIES),
    files    : { ...pickQuota(quotas, QUOTA_CODE.STORAGE), bySection },
  };
};

/**
 * Percentage used, for a progress bar's width - clamped to [0, 100] for
 * DISPLAY only. This is never used to decide whether an action is
 * allowed (the server's *WithQuotaCheck procedures are the only
 * authority on that); it exists purely so a bar never renders past its
 * own edge.
 *
 *   limit === null (unlimited)     -> null - nothing to show a % of
 *   limit === 0    (zero-limit)    -> 100  - nothing is available, full/blocked
 *   limit < 0 or non-finite        -> null - malformed, do not fabricate a number
 *   used === NaN                   -> treated as 0 (unknown usage, not "infinite")
 *   used === +Infinity             -> 100  - unambiguously over, clamp to full rather
 *                                      than divide-by-produces-Infinity
 *
 * @returns {number|null}
 */
export const usagePercent = (used, limit) => {
  if (limit === null || limit === undefined) return null;
  if (!Number.isFinite(limit) || limit < 0) return null;
  if (limit === 0) return 100;
  if (used === Infinity) return 100;
  const safeUsed = Number.isFinite(used) && used > 0 ? used : 0;
  const pct = (safeUsed / limit) * 100;
  // Final safety net - should be unreachable given the guards above, but
  // this function must NEVER hand a NaN/Infinity percentage to a caller
  // that puts it straight into a CSS width or an aria-valuenow.
  if (!Number.isFinite(pct)) return 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

/**
 * How much is left, floored at 0 - "remaining" reads as "how much more
 * you may still use," and a tenant 12 over their limit still has 0 left,
 * never a negative number. `null` for unlimited (nothing to count down
 * from), mirroring usagePercent's convention so a caller can use the
 * same `=== null` check for "show no bar/no count" either way.
 *
 * @returns {number|null}
 */
export const usageRemaining = (used, limit) => {
  if (limit === null || limit === undefined) return null;
  if (!Number.isFinite(limit) || limit < 0) return null;
  const safeUsed = Number.isFinite(used) ? used : 0;
  return Math.max(0, limit - safeUsed);
};

/** True only when there is a real, finite limit AND usage exceeds it. Unlimited is never "over." */
export const isOverQuota = (used, limit) => {
  if (limit === null || limit === undefined || !Number.isFinite(limit)) return false;
  return Number.isFinite(used) && used > limit;
};

/**
 * Human-readable bytes, for the storage quota's used/limit/remaining
 * display - the raw numbers are bytes, and showing "31457280 of
 * 104857600" to a customer would be meaningless.
 */
export const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, exponent);
  // One decimal place only when it's not just trailing ".0" noise (e.g.
  // "1.5 KB" but "30 MB", not "30.0 MB").
  const rounded = exponent === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  const display = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${display} ${units[exponent]}`;
};

/**
 * Total across every "files by section" row - the denominator for each
 * row's "% of total files." 0 when there are no sections at all (never
 * NaN/Infinity from a following division) - see sectionPercent.
 */
export const totalFileCount = (bySection) =>
  Array.isArray(bySection)
    ? bySection.reduce((sum, s) => sum + (Number.isFinite(s && s.count) ? s.count : 0), 0)
    : 0;

/** % of totalFileCount one section represents, clamped [0,100]. `null` when there are no files anywhere (nothing to be a % of). */
export const sectionPercent = (count, total) => {
  if (!Number.isFinite(total) || total <= 0) return null;
  const safeCount = Number.isFinite(count) && count > 0 ? count : 0;
  const pct = (safeCount / total) * 100;
  if (!Number.isFinite(pct)) return null;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

/**
 * Percentages for every "files by section" row at once, guaranteed to
 * sum to exactly 100 (when there's at least one file anywhere) rather
 * than each row rounding independently via sectionPercent - three
 * sections at 1/3 each round to 33% apiece with sectionPercent, reading
 * as "33 + 33 + 33 = 99%" on screen, which is what a user looking
 * closely at "Files by section" would flag as the numbers being wrong.
 * Uses the largest-remainder method: floor every row's raw percentage,
 * then hand the few leftover whole points (100 minus the sum of the
 * floors) to whichever rows had the largest fractional part - the
 * standard way to round a set of percentages that must add up exactly.
 *
 * @param {Array<{name: string, count: number}>} bySection
 * @returns {Array<number|null>} one entry per bySection row, same order;
 *   every entry is `null` when there are no files in any section at all
 *   (nothing to be a % of - same convention as sectionPercent).
 */
export const sectionPercentages = (bySection) => {
  if (!Array.isArray(bySection) || bySection.length === 0) return [];
  const counts = bySection.map(s => (Number.isFinite(s && s.count) && s.count > 0) ? s.count : 0);
  const total = counts.reduce((sum, c) => sum + c, 0);
  if (total <= 0) return counts.map(() => null);

  const raw = counts.map(c => (c / total) * 100);
  const floors = raw.map(Math.floor);
  const remainder = 100 - floors.reduce((sum, f) => sum + f, 0);

  const byFractionDesc = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  const result = floors.slice();
  for (let k = 0; k < remainder && k < byFractionDesc.length; k++) {
    result[byFractionDesc[k].i] += 1;
  }
  return result;
};
